import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Table, TableHead, TableRow, TableCell, TableBody, Box } from '@mui/material';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';

import { DialogUI, InputUI, ButtonUI } from '../../Interface';
import ProductionBatchDialog from './ProductionBatchDialog';
import { DocumentType } from '../../../config/DocumentType';
import productionService from '../../../service/production.service';
import { cleanValues } from '../../../utils/helper';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import { InvManagementMethod } from '../../../config/itemGroup';

const ProductionReceiptDialog = ({
  open,
  onClose,
  selectedValues,
  setSelectedValues,
  setRefetchTable,
  autocompleteData,
}) => {
  const theme = useTheme();
  const ui = theme.palette.ui;
  const [docLines, setDocLines] = useState([]);
  const [batchLine, setBatchLine] = useState(null);

  const defaultBatchLine = {
    id: null,
    batchSn: '',
    lot: '',
    supplierId: '',
    expDate: null,
    mnfDate: null,
    batchAttribute1: '',
    batchAttribute2: '',
    comment: '',
    price: '',
    other: '',
    quantity: '',
  };

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    docLineBatchSn: [],
    docLineBin: [],
  };

  const defaultValues = {
    docTypeId: DocumentType.ReceiptFromProduction,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: selectedValues.whsCode,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const setQty = () => {
    if (!selectedValues?.productionItem) {
      return addNotification({
        content: 'Nincs rögzítve tétel!',
        type: NotificationType.WARNING,
      });
    }

    if (!selectedValues?.whsCode) {
      return addNotification({
        content: 'Nincs rögzítve fej szintű raktár!',
        type: NotificationType.WARNING,
      });
    }
    const emptyLineWhs = selectedValues.productionItem.find((itm) => !itm.whsCode);
    if (emptyLineWhs) {
      addNotification({
        content: 'Nincs rögzítve sor szintű raktár!',
        type: NotificationType.WARNING,
      });
    }

    const newDocLines = [
      {
        ...defaultDocumentLine,
        id: selectedValues.id,
        itemCode: selectedValues.articleNumber,
        itemName: selectedValues.articleName,
        qty: selectedValues.productionQuantity,
        whsCode: selectedValues.whsCode,
        weight: selectedValues.weight || '',
        quantityUnitId: selectedValues.quantityUnit?.id,
        quantityUnitCode: selectedValues.quantityUnit.code,
        quantityUnitName: selectedValues.quantityUnit.name,
        baseId: selectedValues.id,
        baseLine: null,
        baseDocnum: selectedValues.workingNumber,
        baseDocType: null,
      },
      ...selectedValues.productionItem
        .filter((itm) => itm.secondaryProduct)
        .map((itm) => {
          return {
            ...defaultDocumentLine,
            id: itm.id,
            itemCode: itm.itemCode,
            itemName: itm.item?.itemName,
            qty: 0,
            whsCode: itm.whsCode,
            weight: itm.weight || '',
            quantityUnitId: itm.item?.quantityUnit?.id,
            quantityUnitCode: itm.item?.quantityUnit?.code,
            quantityUnitName: itm.item?.quantityUnit?.name,
            baseId: selectedValues.id,
            baseLine: itm.id,
            baseDocnum: selectedValues.workingNumber,
            baseDocType: null,
          };
        }),
    ];

    setDocLines(newDocLines);
  };

  useEffect(() => {
    if (open) {
      setQty();
    } else {
      setDocLines([]);
    }
  }, [open]);

  const handleQuantityChange = (id, value) => {
    const parsed = parseFloat(value);
    setDocLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) return line;
        if (Number.isNaN(parsed)) {
          return { ...line, qty: '' };
        }

        const nextBatch =
          Array.isArray(line.docLineBatchSn) && line.docLineBatchSn.length
            ? [{ ...line.docLineBatchSn[0], quantity: parsed }]
            : line.docLineBatchSn;

        const batchId = nextBatch?.[0]?.id ?? null;
        const nextBins =
          Array.isArray(line.docLineBin) && line.docLineBin.length
            ? line.docLineBin.map((bin) => {
                if (batchId && bin.snBinId !== batchId) return bin;
                return { ...bin, quantity: parsed };
              })
            : line.docLineBin;

        return {
          ...line,
          qty: parsed,
          docLineBatchSn: nextBatch,
          docLineBin: nextBins,
        };
      })
    );
  };

  const handleBatchEdit = (index) => {
    setBatchLine({ ...docLines[index], selectedLineIndex: index });
  };

  const handleBatchSave = (batchList) => {
    if (batchLine && typeof batchLine.selectedLineIndex === 'number') {
      setDocLines((prev) => {
        return prev.map((line, idx) => {
          if (idx === batchLine.selectedLineIndex) {
            const updatedBatch = batchList[0] ? { ...batchList[0], quantity: line.qty } : undefined;
            const nextBatch = updatedBatch ? [updatedBatch] : [];

            const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === line.whsCode);
            const isBinManaged = Boolean(whs?.binManaged);
            const isHeaderWhs = line.whsCode === selectedValues?.whsCode;
            const resolvedBin =
              isBinManaged && isHeaderWhs ? whs?.bin?.find((bin) => bin.binId === selectedValues?.binId) : null;

            const nextBins =
              isBinManaged && resolvedBin
                ? [
                    {
                      id: line.docLineBin?.[0]?.id ?? uuidv4(),
                      binCode: resolvedBin.binCode ?? '',
                      binId: resolvedBin.binId ?? null,
                      snBinId: updatedBatch?.id ?? null,
                      quantity: line.qty ?? '',
                    },
                  ]
                : line.docLineBin ?? [];

            return { ...line, docLineBatchSn: nextBatch, docLineBin: nextBins };
          }
          return line;
        });
      });
    }
    setBatchLine(null);
  };

  const handlePostDocument = () => {
    for (const line of docLines) {
      const item = autocompleteData?.items?.find((i) => i.itemCode === line.itemCode);
      const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === line.whsCode);
      const isBinManaged = Boolean(whs?.binManaged);
      const isHeaderWhs = line.whsCode === selectedValues?.whsCode;
      const resolvedBin =
        isBinManaged && isHeaderWhs ? whs?.bin?.find((bin) => bin.binId === selectedValues?.binId) : null;
      if (item && item.invManagementMethod === InvManagementMethod.BATCH) {
        const batchQty = line.docLineBatchSn && line.docLineBatchSn[0] ? Number(line.docLineBatchSn[0].quantity) : 0;
        if (Number(line.qty) !== batchQty) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkszámú tételhez megadott sarzs mennyisége (${batchQty}) nem egyezik a lejelentett mennyiséggel (${line.qty})!`,
            type: NotificationType.ERROR,
          });
          return;
        }

        if (isBinManaged && isHeaderWhs && !resolvedBin?.binId) {
          addNotification({
            content: `A(z) ${line.itemCode} cikkszámú tételhez rakhely megadása kötelező!`,
            type: NotificationType.ERROR,
          });
          return;
        }
      }

      if (isBinManaged && isHeaderWhs && !resolvedBin?.binId) {
        addNotification({
          content: `A(z) ${line.itemCode} cikkszámú tételhez rakhely megadása kötelező!`,
          type: NotificationType.ERROR,
        });
        return;
      }
    }

    const document = {
      ...cleanValues(defaultValues),
      docLine: docLines
        .filter((l) => l.qty > 0)
        .map((line) => {
          const item = autocompleteData?.items?.find((i) => i.itemCode === line.itemCode);
          const isBatchManaged = item?.invManagementMethod === InvManagementMethod.BATCH;
          const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === line.whsCode);
          const isBinManaged = Boolean(whs?.binManaged);
          const isHeaderWhs = line.whsCode === selectedValues?.whsCode;
          const resolvedBin =
            isBinManaged && isHeaderWhs ? whs?.bin?.find((bin) => bin.binId === selectedValues?.binId) : null;
          const batchId = line.docLineBatchSn?.[0]?.id ?? null;

          const docLineBin =
            isBinManaged && isHeaderWhs && resolvedBin
              ? [
                  {
                    id: line.docLineBin?.[0]?.id ?? uuidv4(),
                    binCode: resolvedBin.binCode ?? '',
                    binId: resolvedBin.binId ?? null,
                    snBinId: isBatchManaged ? batchId : null,
                    quantity: line.qty ?? '',
                  },
                ]
              : [];

          return {
            ...cleanValues(line),
            docLineBatchSn: line.docLineBatchSn.map((batch) => ({
              ...cleanValues(batch),
            })),
            docLineBin: docLineBin.map((bin) => ({
              ...cleanValues(bin),
            })),
          };
        }),
    };

    productionService.createProductionReceipt(document, selectedValues.id).then((data) => {
      onClose();
      setRefetchTable(true);
      setSelectedValues(data);
    });
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent={'Bevételezés'} maxWidth="lg" fullWidth>
      <Table style={{ marginTop: 16 }}>
        <TableHead sx={{ background: ui.tableHeader }}>
          <TableRow>
            <TableCell>Cikkszám</TableCell>
            <TableCell>Megnevezés</TableCell>
            <TableCell>Mennyiség</TableCell>
            <TableCell>Me.</TableCell>
            <TableCell>Sarzs</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {docLines.length > 1 ? (
            <>
              <TableRow key={docLines[0].id}>
                <TableCell>{docLines[0].itemCode}</TableCell>
                <TableCell>{docLines[0].itemName}</TableCell>
                <TableCell style={{ maxWidth: 120 }}>
                  <InputUI
                    type="number"
                    value={docLines[0].qty}
                    onChange={(e) => handleQuantityChange(docLines[0].id, e.target.value)}
                    inputProps={{ min: 0 }}
                    size="small"
                  />
                </TableCell>
                <TableCell>{docLines[0].quantityUnitCode}</TableCell>
                {autocompleteData.items.find((i) => i.itemCode === docLines[0].itemCode).invManagementMethod ===
                  InvManagementMethod.BATCH && (
                  <IconButton size="small" onClick={() => handleBatchEdit(0)}>
                    <EditIcon />
                  </IconButton>
                )}
              </TableRow>
              <TableRow key="selejt-row">
                <TableCell colSpan={5} style={{ fontWeight: 'bold', textAlign: 'left' }}>
                  Selejt
                </TableCell>
              </TableRow>
              {docLines.slice(1).map((itm, idx) => (
                <TableRow key={itm.id}>
                  <TableCell>{itm.itemCode}</TableCell>
                  <TableCell>{itm.itemName}</TableCell>
                  <TableCell style={{ maxWidth: 120 }}>
                    <InputUI
                      type="number"
                      value={itm.qty}
                      onChange={(e) => handleQuantityChange(itm.id, e.target.value)}
                      inputProps={{ min: 0 }}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{itm.quantityUnitCode}</TableCell>
                  <TableCell>
                    {autocompleteData.items.find((i) => i.itemCode === itm.itemCode).invManagementMethod ===
                      InvManagementMethod.BATCH && (
                      <IconButton size="small" onClick={() => handleBatchEdit(idx + 1)}>
                        <EditIcon />
                      </IconButton>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </>
          ) : (
            docLines.map((itm, idx) => (
              <TableRow key={itm.id}>
                <TableCell>{itm.itemCode}</TableCell>
                <TableCell>{itm.itemName}</TableCell>
                <TableCell style={{ maxWidth: 120 }}>
                  <InputUI
                    type="number"
                    value={itm.qty}
                    onChange={(e) => handleQuantityChange(itm.id, e.target.value)}
                    inputProps={{ min: 0 }}
                    size="small"
                  />
                </TableCell>
                <TableCell>{itm.quantityUnitCode}</TableCell>
                <TableCell>
                  {autocompleteData.items.find((i) => i.itemCode === itm.itemCode).invManagementMethod ===
                    InvManagementMethod.BATCH && (
                    <IconButton size="small" onClick={() => handleBatchEdit(idx)}>
                      <EditIcon />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))
          )}
        </TableBody>
      </Table>
      {batchLine && (
        <ProductionBatchDialog
          open={!!batchLine}
          onClose={() => setBatchLine(null)}
          batchList={docLines[batchLine.selectedLineIndex]?.docLineBatchSn || []}
          onSave={(batchList) => handleBatchSave(batchList)}
        />
      )}

      <Box sx={{ px: 4, mt: 8, display: 'flex', justifyContent: 'center', gap: 3 }}>
        <ButtonUI
          text={'Könyvelés'}
          className="bg-success"
          type="submit"
          onClick={() => handlePostDocument()}
        ></ButtonUI>
        <ButtonUI
          text="Mégse"
          className="bg-"
          color="blue-gray"
          type="button"
          variant="gradient"
          onClick={onClose}
        ></ButtonUI>
      </Box>
    </DialogUI>
  );
};

export default ProductionReceiptDialog;
